// @credit: 76a97b1cd4349534e19fb23cf80c4a0f1b1502418015de1e9d68b95c44bc36d433ed49b8
//
// Aphilaty PWA — Events Page
//
// Org → member events. Same moderation pipeline as offers/messages:
// submitContent → AI scan → live `events` collection. Cancel/edit while
// pending review; revoke (soft-delete + instant FCM pull) once live.

import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { subscribeToQuery, callFunction, where, orderBy, limit, AppError }
  from '../../services/firebase';

interface EventListItem {
  id: string;
  orgId: string;
  title: string;
  description?: string;
  location?: string;
  startDate: any;
  endDate?: any;
  allDay: boolean;
  status?: string;
  publishedAt?: any;
  isDeleted: boolean;
}

interface SubmissionDoc {
  id: string;
  status: string;
  createdAt: any;
  contentData?: {
    title?: string;
    description?: string;
    location?: string;
    startDate?: { toDate?: () => Date } | null;
    endDate?: { toDate?: () => Date } | null;
    allDay?: boolean;
    deliveryMode?: 'immediate' | 'batch';
  };
}

// content_submissions statuses worth surfacing to org admins — 'posted' graduates
// to the live events list, 'blocked_csam' routes to the legal/CLO pipeline.
const submissionStatusMeta: Record<string, { label: string; badge: string }> = {
  pending_ai:         { label: 'Submitted · scanning',           badge: 'badge-gray' },
  pending_post:       { label: 'Cleared · posts within 1 hour',  badge: 'badge-blue' },
  held_flagged:       { label: 'Held for review',                badge: 'badge-amber' },
  pending_clo_review: { label: 'Held for review',                badge: 'badge-amber' },
  held_scan_error:    { label: 'Held for review',                badge: 'badge-amber' },
};

// Submissions can only be edited or withdrawn while review is still in
// progress — once a submission reaches 'pending_post' it's "Approved · queued
// for delivery" and can no longer be changed here, only revoked once live.
const isPreReview = (status: string) => status !== 'pending_post';

const toLocalInputValue = (d: Date) => {
  const offset = d.getTimezoneOffset();
  return new Date(d.getTime() - offset * 60000).toISOString().slice(0, 16);
};

export function EventsPage() {
  const { activeOrgId, activeOrg, user, isPlatformAdmin } = useAuth();
  const [events, setEvents] = useState<EventListItem[]>([]);
  const [pendingSubmissions, setPendingSubmissions] = useState<SubmissionDoc[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [compose, setCompose] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [deliveryMode, setDeliveryMode] = useState<'immediate' | 'batch'>('batch');
  const [reminder1, setReminder1] = useState('none');
  const [reminder2, setReminder2] = useState('none');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editingSubmissionId, setEditingSubmissionId] = useState<string | null>(null);

  const nowLocal = new Date().toISOString().slice(0, 16);

  useEffect(() => {
    if (!activeOrgId || !user) return;
    // Platform admins: isPlatformAdmin() is an auth-claims literal, always evaluable
    // at collection query time — existing orgId+sharedWithOrg query works.
    // Org admins/members: must use memberUids array-contains so the security rule
    // can prove access at query evaluation time; filter to activeOrg client-side.
    const constraints = isPlatformAdmin
      ? [where('orgId', '==', activeOrgId), where('sharedWithOrg', '==', true), limit(200)]
      : [where(`memberUidMap.${user.uid}`, '==', true), limit(200)];
    return subscribeToQuery<EventListItem>(
      'events',
      constraints,
      data => {
        setEvents(
          data
            .filter(e => !e.isDeleted && (isPlatformAdmin || e.orgId === activeOrgId))
            .sort((a, b) => (b.startDate?.seconds ?? 0) - (a.startDate?.seconds ?? 0))
        );
        setIsLoading(false);
      },
      err => { console.error('[events subscription]', err); setIsLoading(false); }
    );
  }, [activeOrgId, user?.uid, isPlatformAdmin]);

  // Submissions still moving through the moderation pipeline — not yet live
  useEffect(() => {
    if (!activeOrgId) return;
    return subscribeToQuery<SubmissionDoc>(
      'content_submissions',
      [where('orgId', '==', activeOrgId), where('contentType', '==', 'event'), limit(20)],
      data => setPendingSubmissions(
        data
          .filter(s => submissionStatusMeta[s.status])
          .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0))
      ),
      err => console.error('[events pending subscription]', err)
    );
  }, [activeOrgId]);

  const closeCompose = () => {
    setCompose(false); setEditingSubmissionId(null);
    setTitle(''); setDescription(''); setLocation('');
    setStartDate(''); setEndDate(''); setAllDay(false);
    setDeliveryMode('batch'); setReminder1('none'); setReminder2('none'); setError(null);
  };

  const handleSend = async () => {
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!startDate) { setError('Start date/time is required.'); return; }

    const reminderMinutesBefore = [reminder1, reminder2]
      .map(v => v === 'none' ? null : Number(v))
      .filter((v): v is number => v !== null);

    const contentData = {
      title, description, location,
      startDate: new Date(startDate).getTime(),
      endDate: endDate ? new Date(endDate).getTime() : null,
      allDay, sharedWithOrg: true, deliveryMode,
      ...(reminderMinutesBefore.length > 0 && { reminderMinutesBefore }),
    };

    setIsSubmitting(true); setError(null);
    try {
      if (editingSubmissionId) {
        await callFunction('editSubmission', { submissionId: editingSubmissionId, contentData });
      } else {
        await callFunction('submitContent', {
          orgId: activeOrgId,
          contentType: 'event',
          isUserGenerated: false,
          contentData,
        });
      }
      closeCompose();
      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 8000);
    } catch (err) {
      setError((err as AppError).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmission = (sub: SubmissionDoc) => {
    const cd = sub.contentData;
    if (!cd) return;

    setTitle(cd.title ?? '');
    setDescription(cd.description ?? '');
    setLocation(cd.location ?? '');
    const start = cd.startDate?.toDate?.() ?? null;
    const end = cd.endDate?.toDate?.() ?? null;
    setStartDate(start ? toLocalInputValue(start) : '');
    setEndDate(end ? toLocalInputValue(end) : '');
    setAllDay(cd.allDay ?? false);
    setDeliveryMode(cd.deliveryMode ?? 'batch');
    const reminders = (cd as Record<string, unknown>).reminderMinutesBefore as number[] | undefined;
    setReminder1(reminders?.[0] !== undefined ? String(reminders[0]) : 'none');
    setReminder2(reminders?.[1] !== undefined ? String(reminders[1]) : 'none');
    setEditingSubmissionId(sub.id);
    setError(null); setActionError(null);
    setCompose(true);
  };

  const handleCancelSubmission = async (submissionId: string) => {
    setActionError(null);
    try {
      await callFunction('cancelSubmission', { submissionId });
      if (editingSubmissionId === submissionId) closeCompose();
    } catch (err) {
      setActionError((err as AppError).message);
    }
  };

  const handleRevoke = async (eventId: string) => {
    setActionError(null);
    try {
      await callFunction('revokeContent',
        { contentId: eventId, contentType: 'event', reason: 'admin_revoked' });
    } catch (err) {
      setActionError((err as AppError).message);
    }
  };

  if (!activeOrgId) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
      <p style={{ fontSize: 15, marginBottom: 8 }}>No org selected</p>
      <p style={{ fontSize: 13 }}>Use the org picker in the top nav to select an org, then return here to view and manage its events.</p>
    </div>
  );

  return (
    <div>
      {submitSuccess && (
        <div className="banner banner-success" style={{ marginBottom: 12 }}>
          Your event has been submitted for review. It will appear to members within a few minutes.
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16 }}>
        <h1 style={{ fontSize: 18, fontWeight: 600 }}>Events — {activeOrg?.name}</h1>
        <button className="btn btn-primary" onClick={() => (compose ? closeCompose() : setCompose(true))}>
          {compose ? 'Cancel' : '+ Compose'}
        </button>
      </div>

      {actionError && (
        <div className="error-banner" role="alert" style={{ marginBottom: 16 }}>
          {actionError}
          <button onClick={() => setActionError(null)} style={{ marginLeft: 'auto',
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-danger)', fontSize: 16 }}>×</button>
        </div>
      )}

      {compose && (
        <div className="card" style={{ marginBottom: 16, padding: 18 }}>
          <h2 style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>
            {editingSubmissionId ? 'Edit Event' : 'New Event'}
          </h2>
          {error && <div className="error-banner" role="alert">{error}</div>}
          <div className="form-group">
            <label className="form-label" htmlFor="evt-title">Title</label>
            <input id="evt-title" type="text" className="form-input" value={title}
              onChange={e => setTitle(e.target.value)} placeholder="Fall Open House" />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="evt-description">Description (optional)</label>
            <textarea id="evt-description" className="form-textarea" value={description}
              onChange={e => setDescription(e.target.value)} rows={3}
              placeholder="What members should know…" />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="evt-location">Location (optional)</label>
            <input id="evt-location" type="text" className="form-input" value={location}
              onChange={e => setLocation(e.target.value)} placeholder="Main hall" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 10 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="evt-start">Starts</label>
              <input id="evt-start" type="datetime-local" className="form-input"
                value={startDate} min={nowLocal}
                onChange={e => setStartDate(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="evt-end">Ends (optional)</label>
              <input id="evt-end" type="datetime-local" className="form-input"
                value={endDate} min={startDate || nowLocal}
                onChange={e => setEndDate(e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <input id="evt-allday" type="checkbox" checked={allDay}
              onChange={e => setAllDay(e.target.checked)} />
            <label htmlFor="evt-allday" style={{ fontSize: 13 }}>All-day event</label>
          </div>
          {!allDay && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 10 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" htmlFor="evt-reminder1">Reminder 1</label>
                <select id="evt-reminder1" className="form-input" value={reminder1}
                  onChange={e => setReminder1(e.target.value)}>
                  <option value="none">None</option>
                  <option value="0">At time of event</option>
                  <option value="15">15 minutes before</option>
                  <option value="30">30 minutes before</option>
                  <option value="60">1 hour before</option>
                  <option value="120">2 hours before</option>
                  <option value="1440">1 day before</option>
                  <option value="2880">2 days before</option>
                  <option value="10080">1 week before</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" htmlFor="evt-reminder2">Reminder 2</label>
                <select id="evt-reminder2" className="form-input" value={reminder2}
                  onChange={e => setReminder2(e.target.value)}>
                  <option value="none">None</option>
                  <option value="0">At time of event</option>
                  <option value="15">15 minutes before</option>
                  <option value="30">30 minutes before</option>
                  <option value="60">1 hour before</option>
                  <option value="120">2 hours before</option>
                  <option value="1440">1 day before</option>
                  <option value="2880">2 days before</option>
                  <option value="10080">1 week before</option>
                </select>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center',
            gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
              Delivery:
            </span>
            <div className="flavor-tabs">
              <button className={`ftab${deliveryMode === 'batch' ? ' active' : ''}`}
                onClick={() => setDeliveryMode('batch')}>Batch (Nightly)</button>
              <button className={`ftab${deliveryMode === 'immediate' ? ' active' : ''}`}
                onClick={() => setDeliveryMode('immediate')}>Immediate</button>
            </div>
            <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
              {deliveryMode === 'batch'
                ? 'Delivers tonight at midnight ET'
                : 'Delivers immediately after review'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={handleSend} disabled={isSubmitting}>
              {isSubmitting
                ? <span className="spinner" />
                : editingSubmissionId ? 'Save Changes' : 'Send for Review'}
            </button>
            <button className="btn btn-outline" onClick={closeCompose}>Cancel</button>
          </div>
        </div>
      )}

      {pendingSubmissions.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span className="card-title">Pending Review</span></div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Title / Preview</th>
                <th>Status</th>
                <th>Submitted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pendingSubmissions.map(sub => {
                const meta = submissionStatusMeta[sub.status];
                const desc = sub.contentData?.description ?? '';
                const editable = isPreReview(sub.status);
                return (
                  <tr key={sub.id}>
                    <td>
                      {sub.contentData?.title && (
                        <div style={{ fontSize: 13 }}>{sub.contentData.title}</div>
                      )}
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)',
                        marginTop: 2 }}>
                        {desc.substring(0, 80)}{desc.length > 80 ? '…' : ''}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${meta.badge}`}>{meta.label}</span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap' }}>
                      {sub.createdAt?.toDate?.()?.toLocaleDateString() ?? '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {editable ? (
                        <>
                          <button className="btn btn-outline btn-sm" style={{ marginRight: 6 }}
                            onClick={() => handleEditSubmission(sub)}>
                            Edit
                          </button>
                          <button className="btn btn-outline btn-sm"
                            onClick={() => handleCancelSubmission(sub.id)}
                            style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                          Queued — can only be revoked once live
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        {isLoading ? (
          <div className="empty-state"><span className="spinner" /></div>
        ) : events.length === 0 ? (
          <div className="empty-state">No events yet.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>When</th>
                <th>Location</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {events.map(evt => (
                <tr key={evt.id}>
                  <td>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{evt.title}</div>
                    {evt.description && (
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)',
                        marginTop: 2 }}>
                        {evt.description.substring(0, 80)}{evt.description.length > 80 ? '…' : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-secondary)',
                    whiteSpace: 'nowrap' }}>
                    {evt.startDate?.toDate
                      ? evt.startDate.toDate().toLocaleString(undefined,
                          evt.allDay ? { dateStyle: 'medium' } : { dateStyle: 'medium', timeStyle: 'short' })
                      : '—'}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    {evt.location ?? '—'}
                  </td>
                  <td>
                    <span className={`badge ${evt.status === 'live' ? 'badge-green' : 'badge-gray'}`}>
                      {evt.status ?? 'live'}
                    </span>
                  </td>
                  <td>
                    {(evt.status ?? 'live') === 'live' && (
                      <button className="btn btn-outline btn-sm"
                        onClick={() => handleRevoke(evt.id)}
                        style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
